import React, { useEffect, useState, useRef } from 'react';
import { Container, Image } from 'react-bootstrap';
import BModal from 'react-bootstrap/Modal';
import { useNavigate } from 'react-router-dom';

import 'bootstrap/dist/css/bootstrap.min.css';
import './ProfileAnswers.css';

import logo from "../../../assets/images/MTDlogo.png";
import backarrow from "../../../assets/images/backarrow.jpg";
import responsivebg from "../../../assets/images/responsive-bg.png";

import Stepper from '../Stepper';
import Loader from '../../Utility/Loader';
import { questionsDefaultAnswers } from '../../../utils/constants';
import * as profileActions from "../../../actions/Profile";
import ConfirmationModal from "../../Utility/ConfirmationModal";
import QuestionPrompt from '../../UpdateProfile/screens/EditAnswerAndQuestion/QuestionPrompt';

export default function ProfileAnswers() {
  const [loader, setLoader] = useState(false);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [questionsCount, setQuestionsCount] = useState(0);
  const [answerModalData, setAnswerModalData] = useState({
    heading: "",
    questionId: "",
  });
  const [questions, setQuestions] = useState([]);
  const navigate = useNavigate();
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteQuestionId, setDeleteQuestionId] = useState(null);
  const [refreshQuestions, setRefreshQuestions] = useState(false);
  const isFetched = useRef(false);

  useEffect(() => {
    if (isFetched.current) return;
    isFetched.current = true;

    fetchQuestionsList();
  }, []);

  const fetchQuestionsList = async () => {
    const response = await profileActions.questionAnswersList()
    const data = await response;
    if (response) {
      setQuestions(data.questions);
    }
  }

  useEffect(() => {
    (async () => {
      const response = await profileActions.getQuestionsCount()
      const data = await response
      setQuestionsCount(data.count);
    })()
  }, [refreshQuestions]);

  const refreshQuestionsList = () => {
    setRefreshQuestions(!refreshQuestions)
  }

  const handleDeleteAnswer = (questionId) => {
    setDeleteQuestionId(questionId);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteAnswer = async () => {
    try {
      const response = await profileActions.deleteQuestionAnswer(deleteQuestionId);

      if (response) {
        setQuestions(prevQuestions =>
          prevQuestions?.map(q =>
            q.question_id === deleteQuestionId ? { ...q, answer: "" } : q
          )
        );
        setRefreshQuestions(prev => !prev);
      }
      setShowDeleteConfirmation(false);
      setDeleteQuestionId(null);
    } catch (error) {
      console.error("Error deleting answer:", error);
      setShowDeleteConfirmation(false);
      setDeleteQuestionId(null);
    }
  };

  const cancelDeleteAnswer = () => {
    setShowDeleteConfirmation(false);
    setDeleteQuestionId(null);
  };

  const handleAnswerSave = async (questionId, newAnswer) => {
    setLoader(true);
    try {
      const response = await profileActions.saveQuestionAnswer(questionId, newAnswer);
      if (response) {
        setQuestions((prevQuestions) =>
          prevQuestions.map((q) =>
            q.question_id === questionId ? { ...q, answer: newAnswer } : q
          )
        );
        refreshQuestionsList();
        setShowAnswerModal(false);
      }
    } catch (error) {
      console.error("Error saving question answer:", error);
    } finally {
      setLoader(false);
    }
  }

  return (
    <div className="profile-answers-container">
      {loader && <Loader />}
      <QuestionPrompt
        show={showAnswerModal}
        modalData={answerModalData}
        onSave={handleAnswerSave}
        onHide={() => setShowAnswerModal(false)}
      />
      <AlertModal show={showAlertModal} count={questionsCount} onHide={() => setShowAlertModal(false)} />
      <ConfirmationModal
        show={showDeleteConfirmation}
        onHide={cancelDeleteAnswer}
        title="Delete Answer"
        message="Are you sure you want to delete this answer? This action cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
        cancelLabel="Cancel"
        onConfirm={confirmDeleteAnswer}
      />
      <div className="profile-answers-bg">
        <Image
          className="banner-image"
          src={responsivebg}
          alt="Background"
        ></Image>
      </div>
      <Container className="profile-answers-main">
        <Container className="profile-answers-content">
          <Container className="logo-progressbar">
            <Container className="logo-arrow">
              <Image
                src={backarrow}
                className="backarrow"
                onClick={() => window.history.back()}
                alt="Back Arrow"
              />
              <Image
                src={logo}
                alt="Logo"
                className="logo logo-transparent"
              />
            </Container>
            <Stepper activeStep="ProfileAnswers" />
          </Container>

          <Container className="profile-answers-details-container">
            <Container className="profile-answers-details">
              <div className="container-child1">
                <div className="profile-answers-section">
                  <Container className="profile-answers-text">
                    <p>Write your profile answers</p>
                  </Container>
                  <p className="required-prompts-text">
                    2 prompts are required.
                  </p>
                  <div className="profile-answers-questions-types">
                    <div className="questions-container">
                      {questions?.map((question, index) => (
                        <div
                          tabIndex={0}
                          key={index}
                          className={`question-item ${question.answer ? 'answered' : ''} ${!question.answer && questions.filter(q => q.answer && q.answer.trim() !== '').length >= 2 ? 'disabled' : ''
                            }`}
                          onClick={() => {
                            // Count answered questions
                            const answeredCount = questions.filter(q => q.answer && q.answer.trim() !== '').length;

                            // Allow opening modal if:
                            // 1. This question already has an answer (editing existing answer)
                            // 2. Less than 2 questions are answered (can add new answer)
                            if (question.answer || answeredCount < 2) {
                              setAnswerModalData({
                                question,
                                heading: question.prompt || question.question,
                                refreshpage: refreshQuestionsList,
                                questionId: question.question_id,
                                onAnswerSaved: handleAnswerSave,
                                placeholder: questionsDefaultAnswers[index],
                                answer: question.answer,
                              });
                              setShowAnswerModal(true);
                            } else {
                              // Show alert if trying to add more than 2 answers
                              setShowAlertModal(true);
                            }
                          }}
                        >
                          <div>
                            <div className='profile-answers-text-question'>
                              {question.question}
                            </div>
                            <div className='profile-answers-text-question-answer question-answer-text'>
                              {question.answer
                                ? question.answer.split(' ').slice(0, 10).join(' ') + (question.answer.split(' ').length > 10 ? '...' : '')
                                : questionsDefaultAnswers[index]?.substring(0, 10) + "..."
                              }
                            </div>
                            <div className='profile-answers-text-question-answer'>
                              {question.description}
                            </div>
                          </div>
                          <div className="question-actions">
                            {question?.answer && (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.nativeEvent.stopImmediatePropagation();
                                  handleDeleteAnswer(question.question_id);
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M15 1L1 15M1.00001 1L15 15"
                                    stroke="#424242"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            )}

                            <svg
                              width="22"
                              height="22"
                              viewBox="0 0 22 22"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12.8346 5.5L7.33464 11V14.6667H11.0013L16.5013 9.16667M12.8346 5.5L15.5846 2.75L19.2513 6.41667L16.5013 9.16667M12.8346 5.5L16.5013 9.16667M9.16797 3.66667L3.66797 3.66667L3.66797 18.3333L18.3346 18.3333V12.8333"
                                stroke="url(#paint0_linear_778_6844)"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <defs>
                                <linearGradient
                                  id="paint0_linear_778_6844"
                                  x1="12.0869"
                                  y1="2.10069"
                                  x2="12.0869"
                                  y2="19.9566"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stopColor="#FC8C66" />
                                  <stop offset="1" stopColor="#F76A7B" />
                                </linearGradient>
                              </defs>
                            </svg>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Container>
          </Container>

          <Container className="profile-answers-next-btn">
            <div className="d-flex justify-content-center profile-answers-next-btn">
              <button
                type="submit"
                className="global-next-bottom-fix-btn"
                onClick={() => {
                  if (questionsCount === 2) {
                    navigate("/kids-family");
                  } else {
                    setShowAlertModal(true);
                  }
                }}
              >
                Next
              </button>
            </div>
          </Container>

        </Container>
      </Container>
    </div>
  );
};


function AlertModal({ show, onHide, count }) {
  return (
    <BModal className='customer-modal-without-header-footer' centered show={show} onHide={onHide}>
      <div>
        <p className="alert-modal-text">
          {
            count !== 2 ? "Answer only 2 prompts to continue, please! Contact info (e.g., phone, social handles) will be rejected." : "Please answer only 2 questions"
          }

        </p>
      </div>
      <div className="alert-modal-button-container">
        <button type="submit" className='global-save-button' onClick={() => { onHide(); }}>
          Okay
        </button>
      </div>
    </BModal>
  )
}

